import BaseDataProvider from "./BaseDataProvider";
import { gql } from "apollo-boost";
import _ from "lodash";
import { getOffsetFromParams, getLimitFromParams } from "./utils";
import { zonedTimeToUtc } from "date-fns-tz";

class GamesDataProvider extends BaseDataProvider {
  async getOne(resource, params) {
    const side = `
          id
          season {
            id
            players {
              id
              jerseyNumber
              position
              yearCode
              isActive
              person {
                id
                firstName
                lastName
                name
              }
            }  
            team {
              id
              name
              teamCode
              school {
                name
              }
            }
          }
          football {
            id
            totalPoints
            firstQuarterPoints
            secondQuarterPoints
            thirdQuarterPoints
            fourthQuarterPoints
            overtimePoints
            overtime2Points
            overtime3Points
            passingCompletions
            passingAttempts
            passingYards
            passingTouchdowns
            passingInterceptions
            rushingAttempts
            rushingYards
            rushingTouchdowns
            fumbles
            fumblesLost
            receptions
            receivingYards
            receivingTouchdowns
            fieldGoalsMade
            fieldGoalAttempts
            pointAfterTouchdown
            pointAfterTouchdownAttempts
            pointAfterTouchdownsMade
            interceptionTouchdowns
            pointAfterTouchdownTwoPoints
            returnTouchdowns
            pointAfterTouchdownReturns
            firstDowns
            penalties
            penaltyYards
            pointsAllowed
            puntingYards
            punts
            touchdownPasses
            touchdownReceptions
            totalYards
            yardsAllowed
            puntingAverage
            passingCompletionPercentage
            passingInterceptionYards
            passingAverage
            rushingAverage
            receivingAverage
            fieldGoalYards
          }
          girlsvolleyball {
            id
            game1Points
            game2Points
            game3Points
            game4Points
            game5Points
            finalScore
            kills
            aces
            blocks
            digs
            assists
          }
          basketball {
            id
            totalPoints
            firstQuarterPoints
            secondQuarterPoints
            thirdQuarterPoints
            fourthQuarterPoints
            overtimePoints
            overtime2Points
            overtime3Points
            rebounds
            threePointFieldGoals
          }
          soccer {
            id
            totalPoints
            firstHalfGoals
            secondHalfGoals
            overtimeGoals
            overtime2Goals
            shootouts
            totalGoals
          }
          baseball {
            id
            runs
            hits
            errors
          }
          players {
            id
            player {
              id
              person {
                id
                name
              }
            }
          }
          `;

    const GET = gql`
          query {
            GameByID(id:${params.id}) {
              id
              gameStatus
              gameDateString
              gameTimeString
              gameIsDistrict
              playoffRound
              sportCode
              seasonCode
              playsBlob
              gameStory
              gameStoryIsPublished
              internalNotes
              homeTeamCode
              awayTeamCode
              isTopGame
              metadata
              location {
                id
                stableId
                name
                abbr
              }
              home {
                ${side}
              }
              away {
                ${side}
              }
            }
          }`;

    try {
      const response = await this.client.query({ query: GET });
      
      const gameData = response.data.GameByID;
      
      if (gameData.metadata) {
        try {
          gameData.metadata = JSON.parse(gameData.metadata);
        } catch (parseError) {
          console.warn("bad game metadata field:", parseError);
        }
      }
      
      return { data: gameData };
    } catch (err) {
      console.error(JSON.stringify(err, null, 2));
      return null;
    }
  }

  async getList(resource, params) {
    const offset = getOffsetFromParams(params);
    const limit = getLimitFromParams(params);
    const name = _.get(params, "filter.name", "");
    const startDate = _.get(params, "filter.startDate", "");
    const endDate = _.get(params, "filter.endDate", "");
    const sportCode = _.get(params, "filter.sportCode", "");
    const seasonCode = _.get(params, "filter.seasonCode", "");
    const isTopGame = _.get(params, "filter.isTopGame", "");
    const startDateUtc = startDate
      ? zonedTimeToUtc(`${startDate} 00:00:00`, "America/Chicago")
      : "";
    const endDateUtc = endDate
      ? zonedTimeToUtc(`${endDate} 23:59:59`, "America/Chicago")
      : "";

    const paramsString = `
          name:"${name === "%%" ? "" : name}"
          startDate:"${startDateUtc}"
          endDate:"${endDateUtc}"
          sportCode:"${sportCode}"
          seasonCode:"${seasonCode}"
          offset:${offset}
          limit:${limit}
          isTopGame:${typeof isTopGame === "boolean" ? isTopGame : null}
        `;

    const side = `
          season {
            team {
              id
              name
              school {
                name
              }
            }
          }
          football {
            totalPoints
          }
          girlsvolleyball {
            finalScore
          }
          basketball {
            totalPoints
          }
          soccer {
            totalGoals
          }
          baseball {
            runs
          }
        `;

    const GET = gql`
          query {
            GamesSearch(${paramsString}) {
              total
              offset
              limit
              data {
                id
                gameStatus
                gameDateString
                gameTimeString
                gameDate
                sportCode
                isTopGame
                home {
                  ${side}
                }
                away {
                  ${side}
                }
              }
            }
          }`;
    try {
      const response = await this.client.query({ query: GET });
      return response.data.GamesSearch;
    } catch (err) {
      console.error(JSON.stringify(err, null, 2));
      return null;
    }
  }

  async update(resource, params) {
    const data = _.get(params, "data", {});
    const isPlaysBlobEdit = _.size(data) === 1;

    let paramsString = `id: ${params.id}`;

    const {
      location,
      gameDateString = "",
      gameTimeString = "",
      gameIsDistrict = false,
      playoffRound = null,
      gameStory = null,
      gameStoryIsPublished = false,
      internalNotes = null,
      playsBlob = "",
      isTopGame = false,
      metadata = null
    } = data;

    const locationId = _.get(location, "id", location);

    // metadata transferred as a string which is eventually re-escaped
    // again and embedded in the graphql request. this actually makes
    // sense if you trace it all through but is confusing.
    const metadataString = metadata ? JSON.stringify(metadata) : null;

    if (isPlaysBlobEdit) {
      paramsString += `
          playsBlob: ${JSON.stringify(playsBlob)}
          `;
    } else {
      paramsString += `
          location: ${locationId ? `"${locationId}"` : null}
          gameDateString: ${gameDateString ? `"${gameDateString}"` : null}
          gameTimeString: ${gameTimeString ? `"${gameTimeString}"` : null}
          gameStory: ${gameStory ? JSON.stringify(gameStory) : null}
          internalNotes: ${internalNotes ? JSON.stringify(internalNotes) : null}
          gameIsDistrict: ${gameIsDistrict}
          playoffRound: ${playoffRound}
          gameStoryIsPublished: ${gameStoryIsPublished}
          playsBlob: ${JSON.stringify(playsBlob)}
          isTopGame: ${isTopGame}
          metadata: ${metadataString ? JSON.stringify(metadataString) : null}
          `;
    }

    const side = `
          season {
            players {
              id
              jerseyNumber
              person {
                id
                firstName
                lastName
                name
              }
            }
            id
            team {
              id
              name
              teamCode
              school {
                name
              }
            }
          }
          girlsvolleyball {
            id
            game1Points
            game2Points
            game3Points
            game4Points
            game5Points
            finalScore
            kills
            aces
            blocks
            digs
            assists
          }
          basketball {
            id
            totalPoints
            firstQuarterPoints
            secondQuarterPoints
            thirdQuarterPoints
            fourthQuarterPoints
            overtimePoints
            overtime2Points
            overtime3Points
            rebounds
            threePointFieldGoals
          }
          soccer {
            id
            totalPoints
            firstHalfGoals
            secondHalfGoals
            overtimeGoals
            overtime2Goals
            shootouts
            totalGoals
          }
          baseball {
            id
            runs
            hits
            errors
          }
          players {
            id
            player {
              id
              jerseyNumber
              person {
                id
                firstName
                lastName
                name
              }
            }
          }
          `;

    const MUTATION = gql`
          mutation {
            UpdateGame(
              ${paramsString}
            ) {
              id
              gameStatus
              gameDateString
              gameTimeString
              gameIsDistrict
              playoffRound
              playsBlob
              gameStory
              gameStoryIsPublished
              internalNotes
              sportCode
              seasonCode
              homeTeamCode
              awayTeamCode
              isTopGame
              metadata
              location {
                id
                stableId
                name
                abbr
              }
              home {
                ${side}
              }
              away {
                ${side}
              }
            }
          }`;

    try {
      const response = await this.client.mutate({ mutation: MUTATION });
      
      const gameData = response.data.UpdateGame;
      
      // Parse metadata string to JSON object if it exists
      if (gameData.metadata) {
        try {
          gameData.metadata = JSON.parse(gameData.metadata);
        } catch (parseError) {
          console.warn("Failed to parse metadata JSON:", parseError);
          // Keep as string if parsing fails
        }
      }
      
      return { data: gameData };
    } catch (err) {
      console.error(JSON.stringify(err, null, 2));
      return null;
    }
  }

  async getManyReference(resource, params) {
    if (params.target === "school_sport_season_team_id")
      return this.getManyReferencesViaSeasonTeam(resource, params);
    throw new Error("BAD TARGET");
  }

  async getManyReferencesViaSeasonTeam(resource, params) {
    // TODO: should be cased out! see notes on other gMR implementations.
    const GET = gql`
          query {
            SeasonTeamByID(id:"${params.id}") {
              seasonCode
              sportCode
              teamGames(startDate:null, endDate:null) {
                id
                game {
                  id
                  gameStatus
                  gameDateString
                  home {
                    season {
                      team {
                        id
                        name
                        school {
                          name
                        }
                      }
                    }
                    football {
                      id
                      totalPoints
                      firstQuarterPoints
                      secondQuarterPoints
                      thirdQuarterPoints
                      fourthQuarterPoints
                      overtimePoints
                      overtime2Points
                      overtime3Points
                      passingCompletions
                      passingAttempts
                      passingYards
                      passingTouchdowns
                      passingInterceptions
                      rushingAttempts
                      rushingYards
                      rushingTouchdowns
                      fumbles
                      fumblesLost
                      receptions
                      receivingYards
                      receivingTouchdowns
                      fieldGoalsMade
                      fieldGoalAttempts
                      pointAfterTouchdown
                      pointAfterTouchdownAttempts
                      pointAfterTouchdownsMade
                      interceptionTouchdowns
                      pointAfterTouchdownTwoPoints
                      returnTouchdowns
                      pointAfterTouchdownReturns
                      firstDowns
                      penalties
                      penaltyYards
                      pointsAllowed
                      puntingYards
                      punts
                      touchdownPasses
                      touchdownReceptions
                      totalYards
                      yardsAllowed
                      puntingAverage
                      passingCompletionPercentage
                      passingInterceptionYards
                      passingAverage
                      rushingAverage
                      receivingAverage
                      fieldGoalYards
                    }
                    girlsvolleyball {
                      id
                      finalScore
                      game1Points
                      game2Points
                      game3Points
                      game4Points
                      game5Points
                      kills
                      aces
                      blocks
                      digs
                      assists
                    }
                    basketball {
                      id
                      totalPoints
                      firstQuarterPoints
                      secondQuarterPoints
                      thirdQuarterPoints
                      fourthQuarterPoints
                      overtimePoints
                      overtime2Points
                      overtime3Points
                      rebounds
                      threePointFieldGoals
                    }
                    soccer {
                      id
                      totalPoints
                      firstHalfGoals
                      secondHalfGoals
                      overtimeGoals
                      overtime2Goals
                      shootouts
                      totalGoals
                    }
                    baseball {
                      id
                      runs
                      hits
                      errors
                    }
                  }
                  away {
                    season {
                      team {
                        id
                        name
                        school {
                          name
                        }
                      }
                    }
                    football {
                      id
                      totalPoints
                      firstQuarterPoints
                      secondQuarterPoints
                      thirdQuarterPoints
                      fourthQuarterPoints
                      overtimePoints
                      overtime2Points
                      overtime3Points
                      passingCompletions
                      passingAttempts
                      passingYards
                      passingTouchdowns
                      passingInterceptions
                      rushingAttempts
                      rushingYards
                      rushingTouchdowns
                      fumbles
                      fumblesLost
                      receptions
                      receivingYards
                      receivingTouchdowns
                      fieldGoalsMade
                      fieldGoalAttempts
                      pointAfterTouchdown
                      pointAfterTouchdownAttempts
                      pointAfterTouchdownsMade
                      interceptionTouchdowns
                      pointAfterTouchdownTwoPoints
                      returnTouchdowns
                      pointAfterTouchdownReturns
                      firstDowns
                      penalties
                      penaltyYards
                      pointsAllowed
                      puntingYards
                      punts
                      touchdownPasses
                      touchdownReceptions
                      totalYards
                      yardsAllowed
                      puntingAverage
                      passingCompletionPercentage
                      passingInterceptionYards
                      passingAverage
                      rushingAverage
                      receivingAverage
                      fieldGoalYards
                    }
                    girlsvolleyball {
                      id
                      finalScore
                      game1Points
                      game2Points
                      game3Points
                      game4Points
                      game5Points
                      kills
                      aces
                      blocks
                      digs
                      assists
                    }
                    basketball {
                      id
                      totalPoints
                      firstQuarterPoints
                      secondQuarterPoints
                      thirdQuarterPoints
                      fourthQuarterPoints
                      overtimePoints
                      overtime2Points
                      overtime3Points
                      rebounds
                      threePointFieldGoals
                    }
                    soccer {
                      id
                      totalPoints
                      firstHalfGoals
                      secondHalfGoals
                      overtimeGoals
                      overtime2Goals
                      shootouts
                      totalGoals
                    }
                    baseball {
                      id
                      runs
                      hits
                      errors
                    }
                  }
                }
              }
            }
          }`;
    try {
      const response = await this.client.query({ query: GET });
      const data = response.data.SeasonTeamByID.teamGames.map((tg) => tg.game);
      return { data: data };
    } catch (err) {
      console.error(JSON.stringify(err, null, 2));
      return null;
    }
  }

  async create(resource, params) {
    const data = _.get(params, "data", {});
    const {
      homeSeasonTeam = "",
      awaySeasonTeam = "",
      location,
      date = "",
      time = "",
      gameIsDistrict = false,
      playoffRound = null,
      isTopGame = false,
      metadata = null
    } = data;

    const gameStatus = date ? "SCHEDULED" : "PRELIMINARY";
    
    // Convert metadata object to JSON string if it exists
    const metadataString = metadata ? JSON.stringify(metadata) : null;

    const CREATE_OBJECT = gql`
          mutation {
            CreateGame(
              ${homeSeasonTeam ? `homeSeasonTeam: "${homeSeasonTeam}"` : ""}
              ${awaySeasonTeam ? `awaySeasonTeam: "${awaySeasonTeam}"` : ""}
              ${location ? `location: "${location}"` : ""}
              ${date ? `date: "${date}"` : ""}
              ${time ? `time: "${time}"` : ""}
              ${gameStatus ? `gameStatus: ${gameStatus}` : ""}
              gameIsDistrict: ${gameIsDistrict}
              playoffRound: ${playoffRound}
              isTopGame: ${typeof isTopGame === "boolean" ? isTopGame : null}
              ${metadataString ? `metadata: ${JSON.stringify(metadataString)}` : ""}
            ) {
              id
            }
          }`;
    const response = await this.client.mutate({ mutation: CREATE_OBJECT });
    return { data: response.data.CreateGame };
  }

  async delete(resource, params) {
    const DESTROY_OBJECT = gql`
            mutation {
              DeleteGameByID(id: ${_.get(params, "id", "")})
            }`;
    const response = await this.client.mutate({ mutation: DESTROY_OBJECT });
    if (!response) throw new Error("BAD:", JSON.stringify(response));
    return { data: params.previousData };
  }
}

export default GamesDataProvider;
